import { useEffect, useRef, useState } from 'react'

import { useField } from '@unform/core'
import { IconCaretDownSmall, IconCaretUpSmall } from '@vtex/shoreline'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { INTEREST_CATEGORIES } from '~/modules/retailMedia/constants/audienceTargeting'

import { CATEGORY_ICON_BY_NAME } from './categoryIcons'

import {
  CategoryCard,
  CategoryDescription,
  CategoryGrid,
  CategoryHeader,
  CategoryHeaderMeta,
  CategoryIconTile,
  CategoryTitle,
  CategoryTitleBlock,
  CounterBadge,
  SubgroupList,
  ToggleButton,
  ToggleGroup
} from './styles'

type TranslateFn = ReturnType<typeof useAPPTranslation>['t']

/**
 * i18next's generated key type is a closed literal union built from
 * `en.json` — it can't express a key assembled at runtime from a category
 * id (a plain `string`, not one of the finite literal unions the
 * demographic fields above use). This isolates the resulting cast to one
 * place instead of scattering `as never` across every `t()` call site.
 */
const translateDynamicKey = (t: TranslateFn, key: string): string =>
  (t as unknown as (key: string) => string)(key)

type InterestsProps = {
  onChange?(value: string[]): void
}

/**
 * Interest segmentation grid (FR-017a): 19 categories / 74 sub-groups. The
 * sub-group is the selectable unit; the category only groups, expands and
 * shows the selected-count — it is never itself a value. Layout, icons and
 * the round counter badge match the prototype (ads-design-prototypes,
 * feat/offsite): 2-column card grid, icon tile, and sub-group pills
 * (reusing the same ToggleButton as the demographic fields above).
 */
const Interests: React.FC<InterestsProps> = ({ onChange }) => {
  const { t } = useAPPTranslation()
  const { fieldName, registerField, defaultValue } = useField('interests')

  const [selected, setSelected] = useState<string[]>(defaultValue ?? [])
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])

  const selectedRef = useRef(selected)
  selectedRef.current = selected

  const emitChange = (next: string[]) => {
    selectedRef.current = next
    setSelected(next)
    onChange?.(next)
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectedRef,
      getValue: ref => ref.current,
      setValue: (ref, value: string[]) => {
        const next = value ?? []
        ref.current = next
        setSelected(next)
        onChange?.(next)
      },
      clearValue: ref => {
        ref.current = []
        setSelected([])
        onChange?.([])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, registerField])

  const toggleSubgroup = (subgroupId: string) => {
    emitChange(
      selectedRef.current.includes(subgroupId)
        ? selectedRef.current.filter(item => item !== subgroupId)
        : [...selectedRef.current, subgroupId]
    )
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(current =>
      current.includes(categoryId)
        ? current.filter(item => item !== categoryId)
        : [...current, categoryId]
    )
  }

  return (
    <CategoryGrid>
      {INTEREST_CATEGORIES.map(category => {
        const isExpanded = expandedCategories.includes(category.id)
        const selectedCount = category.subgroups.filter(subgroup =>
          selected.includes(subgroup.id)
        ).length
        const CategoryIcon = CATEGORY_ICON_BY_NAME[category.icon]
        // Dynamic i18n keys, one per category/sub-group id — not the finite
        // literal union i18next's types expect (unlike the demographic
        // fields above, whose keys come from a closed string-union type).
        // `translateDynamicKey` isolates the cast to a single helper instead
        // of scattering `as never` across each call site.
        const categoryKeyPrefix = `rm:campaign.create.AudienceTargeting.interests.category.${category.id}`
        const categoryTitle = translateDynamicKey(
          t,
          `${categoryKeyPrefix}.title`
        )
        const categoryDescription = translateDynamicKey(
          t,
          `${categoryKeyPrefix}.description`
        )

        return (
          <CategoryCard key={category.id} data-expanded={isExpanded}>
            <CategoryHeader
              type="button"
              onClick={() => toggleCategory(category.id)}
              aria-expanded={isExpanded}
            >
              {CategoryIcon && (
                <CategoryIconTile aria-hidden="true">
                  <CategoryIcon />
                </CategoryIconTile>
              )}

              <CategoryTitleBlock>
                <CategoryTitle>{categoryTitle}</CategoryTitle>
                <CategoryDescription>{categoryDescription}</CategoryDescription>
              </CategoryTitleBlock>

              <CategoryHeaderMeta>
                {!!selectedCount && (
                  <CounterBadge>{selectedCount}</CounterBadge>
                )}
                {isExpanded ? <IconCaretUpSmall /> : <IconCaretDownSmall />}
              </CategoryHeaderMeta>
            </CategoryHeader>

            {isExpanded && (
              <SubgroupList>
                <ToggleGroup role="group" aria-label={categoryTitle}>
                  {category.subgroups.map(subgroup => (
                    <ToggleButton
                      key={subgroup.id}
                      type="button"
                      data-selected={selected.includes(subgroup.id)}
                      aria-pressed={selected.includes(subgroup.id)}
                      onClick={() => toggleSubgroup(subgroup.id)}
                    >
                      {translateDynamicKey(
                        t,
                        `${categoryKeyPrefix}.subgroup.${subgroup.id}`
                      )}
                    </ToggleButton>
                  ))}
                </ToggleGroup>
              </SubgroupList>
            )}
          </CategoryCard>
        )
      })}
    </CategoryGrid>
  )
}

export default Interests
